import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import {
	collectionCount,
	collectionData,
	docData,
} from '@marmon/frontend/firebase/firestore';
import {
	AlgoliaFetch,
	AlgoliaService,
	Fetchable,
	PaginatedItem,
	PaginatedObject,
	PaginatedOptions,
} from '@marmon/frontend/pagination';
import { Activity, ActivityType } from '@marmon/shared/types/activity';
import { AlgoliaEntity, AlgoliaIndex } from '@marmon/shared/types/algolia';
import { Entity } from '@marmon/shared/types/common';
import { SortDir } from '@marmon/shared/types/pagination';
import { sanitize } from '@marmon/shared/utils';
import {
	collection,
	CollectionReference,
	doc,
	documentId,
	DocumentReference,
	DocumentSnapshot,
	getDoc,
	getDocs,
	limit,
	orderBy,
	query,
	QueryConstraint,
	setDoc,
	startAfter,
	Timestamp,
	UpdateData,
	updateDoc,
	where,
	WithFieldValue,
	writeBatch,
} from 'firebase/firestore';
import { combineLatest, firstValueFrom, Subject } from 'rxjs';
import { API_URL } from '../../tokens';
import {
	ApplyActivityService,
	LogActivityPayload,
} from './apply-activity.service';

type CrudServiceConfig = {
	version?: 'v1';
	algoliaIndex?: AlgoliaIndex;
	initialize?: boolean;
};

const DefaultConfig: CrudServiceConfig = { version: 'v1', initialize: true };

export abstract class CrudService<
	ObjectType extends Entity,
	AlgoliaType extends AlgoliaEntity = AlgoliaEntity,
>
	extends ApplyActivityService<ObjectType>
	implements Fetchable<ObjectType>
{
	protected static MaxBatch = 500;

	collection!: CollectionReference<ObjectType, ObjectType>;

	protected readonly items = new Map<string, ObjectType>();

	protected readonly api = inject(API_URL);
	protected readonly http = inject(HttpClient);
	protected readonly algolia = inject(AlgoliaService);

	/**
	 * emits whenever a change event occurs locally. That way if a modal or some out of scope
	 * service/component creates content we can have other components/table listen for it
	 * to know when we need to reload.
	 */
	readonly changed$ = new Subject<void>();

	readonly reset$ = new Subject<boolean>();

	get config() {
		return Object.assign({}, DefaultConfig, this._config);
	}

	constructor(
		protected path?: string,
		protected readonly _config?: CrudServiceConfig,
	) {
		super();
		if (this.config.initialize) this.setupInitializing();
	}

	setupInitializing = () =>
		combineLatest([this.user$]).subscribe(() => {
			// resets when the authenticated user changes
			this.reset$.next(true);
			this.initialize();
		});

	protected initialize() {
		if (!this.path) return;

		this.collection = collection(
			this.firestore,
			`${this.path}`,
		) as CollectionReference<ObjectType, ObjectType>;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	async create(payload: Partial<ObjectType>, _options?: object) {
		const now = Timestamp.now();
		payload.createdAt = now;
		payload.updatedAt = now;
		payload.deletedAt = null;

		payload.createdBy = this.userRef$.value ?? null;

		payload.id = payload.id ?? crypto.randomUUID();

		const docRef = doc(this.collection, payload.id);
		// we use `setDoc` instead of `addDoc` to use our own `id` value.
		await setDoc(
			docRef,
			sanitize<WithFieldValue<Partial<ObjectType>>>(payload),
		);

		if (this.user$.value?.id) {
			await this.logActivity({
				entity: docRef,
				activity: { type: 'create' },
				createdBy: this.userRef$.value ?? this.user$.value?.id,
			});
		}

		this.changed$.next();
		this.clearAlgoliaCache();
		return payload;
	}

	async update(
		payload: Partial<ObjectType & Entity>,
		options: UpdateOptions,
	): Promise<Partial<ObjectType>> {
		payload.updatedAt = Timestamp.now();

		const docRef = doc(this.collection, payload.id);
		const value = sanitize(payload);
		await updateDoc(docRef, value as UpdateData<ObjectType>);

		if (this.user$.value?.id && options.addActivityLog) {
			await this.logActivity({
				entity: docRef,
				activity: { type: options.type },
				createdBy: this.userRef$.value ?? this.user$.value?.id,
			});
		}

		this.changed$.next();

		// after the update we need to refetch the data
		if (payload.id) this.items.delete(payload.id);
		this.clearAlgoliaCache();

		return value;
	}

	async delete(id: string) {
		const docRef = doc(this.collection, id);

		const now = Timestamp.now();
		await updateDoc(
			docRef,
			sanitize({
				updatedAt: now,
				deletedAt: now,
			}) as UpdateData<ObjectType>,
		);

		if (this.user$.value?.id) {
			await this.logActivity({
				entity: docRef,
				activity: { type: 'delete' },
				createdBy: this.userRef$.value ?? this.user$.value?.id,
			});
		}

		this.changed$.next();
		this.clearAlgoliaCache();
		// empty object to ensure deletion success
		return {};
	}

	/**
	 * Batch creates multiple entries at the same time.
	 *
	 * @param payloads The collection of entries to create.
	 */
	batchCreate(payloads: (ObjectType & Entity)[]) {
		if (payloads.length > CrudService.MaxBatch) {
			// todo: setup true batching on limits
			throw new Error(
				'Attempting to batch update more than 500 records.',
			);
		}

		const batch = writeBatch(this.firestore);
		const activityTasks: Promise<Activity>[] = [];

		const now = Timestamp.now();
		payloads.forEach((payload) => {
			payload.createdAt = now;
			payload.updatedAt = now;
			payload.deletedAt = null;
			payload.id = crypto.randomUUID();

			const docRef = doc(this.collection, payload.id);
			batch.set(docRef, sanitize(payload));
			if (this.user$.value?.id) {
				activityTasks.push(
					this.logActivity({
						entity: docRef,
						activity: { type: 'create' },
						createdBy: this.userRef$.value ?? this.user$.value?.id,
					}),
				);
			}
		});

		// we need the activity promises to complete AFTER the entity actions occur or
		// a race case could occur and we add notifications to an object that doesn't
		// exist yet
		return batch.commit().then(() => {
			// we don't need to wait for this to complete
			Promise.all(activityTasks);
			this.changed$.next();
			return payloads;
		});
	}

	/**
	 * Batch updates multiple entries at the same time.
	 *
	 * @param payloads The collection of entries to create.
	 */
	batchUpdate(
		payloads: Partial<ObjectType & Entity>[],
		options: UpdateOptions,
	) {
		if (payloads.length > CrudService.MaxBatch) {
			// todo: setup true batching on limits
			throw new Error(
				'Attempting to batch update more than 500 records.',
			);
		}

		const batch = writeBatch(this.firestore);
		const activityTasks: Promise<Activity>[] = [];

		const now = Timestamp.now();
		payloads.forEach((payload) => {
			payload.updatedAt = now;

			const docRef = doc(this.collection, payload.id);
			batch.update(docRef, sanitize(payload) as UpdateData<ObjectType>);
			if (this.user$.value?.id && options.addActivityLog) {
				activityTasks.push(
					this.logActivity({
						entity: docRef,
						activity: { type: options.type },
						createdBy: this.userRef$.value ?? this.user$.value?.id,
					}),
				);
			}
		});

		// we need the activity promises to complete AFTER the entity actions occur or
		// a race case could occur and we add notifications to an object that doesn't
		// exist yet
		return batch.commit().then(() => {
			// we don't need to wait for this to complete
			Promise.all(activityTasks);
			this.changed$.next();
			return payloads;
		});
	}

	/**
	 * Batch updates multiple entries at the same time.
	 *
	 * @param payloads The collection of entries to create.
	 */
	batchDelete(payloads: Partial<ObjectType & Entity>[]) {
		if (payloads.length > CrudService.MaxBatch) {
			// todo: setup true batching on limits
			throw new Error(
				'Attempting to batch update more than 500 records.',
			);
		}

		const batch = writeBatch(this.firestore);
		const activityTasks: Promise<Activity>[] = [];

		const now = Timestamp.now();
		payloads.forEach((payload) => {
			const docRef = doc(this.collection, payload.id);
			batch.update(docRef, { deletedAt: now } as UpdateData<ObjectType>);
			if (this.user$.value?.id) {
				activityTasks.push(
					this.logActivity({
						entity: docRef,
						activity: { type: 'delete' },
						createdBy: this.userRef$.value ?? this.user$.value?.id,
					}),
				);
			}
		});

		// we need the activity promises to complete AFTER the entity actions occur or
		// a race case could occur and we add notifications to an object that doesn't
		// exist yet
		return batch.commit().then(() => {
			// we don't need to wait for this to complete
			Promise.all(activityTasks);
			this.changed$.next();
			return payloads;
		});
	}

	/**
	 * Finds a collection's document record by it's id
	 *
	 * @param id The unique id of the document.
	 */
	findById(id: string, useCache = true) {
		if (useCache && this.items.has(id)) {
			return Promise.resolve(this.items.get(id) as ObjectType);
		}

		return getDoc(doc(this.collection, id)).then((doc) => {
			if (!doc.exists()) throw new Error('Document does not exist');
			const data = doc.data() as ObjectType;
			this.items.set(id, data);
			return data;
		});
	}

	getDocRef(id: string): DocumentReference<ObjectType> {
		return doc(this.collection, id);
	}

	/**
	 * Fetches all the records that match the uid
	 *
	 * @param uids The unique identifiers to fetch.
	 */
	async fetchByIdentities(uids: string[] = []) {
		if (uids.length === 0) return [];

		// This is a limitation on Firebase. You can only use the `in` Filter Operation with a max of a 30 length array
		const BATCH_SIZE = 30;
		const totalBatches = Math.ceil(uids.length / BATCH_SIZE);

		const value = [];
		for (let i = 0; i < totalBatches; i++) {
			const batchIds = uids.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE);

			const queryDocs = query(
				this.collection,
				where(documentId(), 'in', batchIds),
			);

			const { docs } = await getDocs(queryDocs);
			value.push(...docs.map((doc) => doc.data() as ObjectType));
		}
		return value;
	}

	/**
	 * Fetches all the document records in a collection as their raw reference payload. This
	 * may be needed in order to set the references properly on other documents.
	 */
	fetchReferences(options?: PaginatedOptions<ObjectType>) {
		const sortDir = options?.sortDir ?? 'asc';
		const sortBy = options?.sortBy ?? 'createdAt';

		const constraints: QueryConstraint[] = [
			orderBy(sortBy as string, sortDir),
			where('deletedAt', '==', null),
		];

		if (options?.constraints) constraints.push(...options.constraints);

		query(this.collection, ...constraints);
		return getDocs(query(this.collection, ...constraints));
	}

	async fetch<T extends Pick<Entity, 'id'>>(
		paginated: PaginatedObject<T>,
		options?: {
			subcollections?: { name: string; docId: string }[];
			next?: boolean;
		},
	): Promise<PaginatedObject<T>> {
		const { subcollections, next } = options ?? {};

		let collectionRef!: CollectionReference;
		if (subcollections?.length) {
			subcollections.forEach(({ docId, name }) => {
				const docRef = collectionRef
					? doc(collectionRef, docId)
					: doc(this.collection, docId);
				collectionRef = collection(docRef, name);
			});
		} else {
			collectionRef = this.collection;
		}

		paginated.totalElements = await firstValueFrom(
			collectionCount(query(collectionRef, ...paginated.constraints)),
		);

		const constraints = [...paginated.constraints];

		if (next) {
			const lastDoc = paginated.last ?? null;
			if (lastDoc) constraints.push(startAfter(lastDoc));
		}

		if (paginated.pageSize) constraints.push(limit(paginated.pageSize));

		const snapshot = await getDocs(query(collectionRef, ...constraints));

		let last: DocumentSnapshot | null = null;
		const items = snapshot.docs.map((d) => {
			last = d;
			return {
				id: d.id,
				ref: d.ref,
				data: d.data() as T,
			} as PaginatedItem<T>;
		});

		if (options?.next) {
			paginated.update({ last });
			paginated.add(items);
		} else {
			paginated.update({ last, items });
		}

		return paginated;
	}

	/**
	 * Fetches the data from the initializing collection and leaves the stream open for the
	 * implementation to determine how to handle it. Useful for needing immediate updating
	 * for new data like alerting users when a new notification comes through
	 */
	fetchStream(options?: { sortBy?: keyof ObjectType; sortDir?: SortDir }) {
		const constraints: QueryConstraint[] = [where('deletedAt', '==', null)];

		// only allow strings for the sortBy value
		if (options?.sortBy && typeof options.sortBy === 'string')
			constraints.push(orderBy(options.sortBy, options.sortDir ?? 'asc'));

		return collectionData<ObjectType>(
			query(this.collection, ...constraints),
		);
	}

	/**
	 * Fetches all of the Algolia entities based on the provided options in the fetch and
	 * returns the hits. Query is required, even if it is just an empty search string.
	 *
	 * @param options the options associated with the requested fetch
	 * @returns Array of algolia hits
	 */
	fetchAlgolia<T extends AlgoliaType = AlgoliaType>(
		options: Omit<AlgoliaFetch<T>, 'index' | 'user'>,
	) {
		if (!this.config.algoliaIndex)
			throw Error('Algolia Index was not provided by implementation');

		const user = this.user$.value;
		if (!user) throw Error('No authenticated user was found');

		return this.algolia.fetch<T>({
			...options,
			index: this.config.algoliaIndex,
			user,
		});
	}

	/**
	 * Returns the unique values for an Algolia facet without loading hits.
	 */
	fetchFacetValues(
		facet: string,
		options: Omit<
			AlgoliaFetch<AlgoliaType>,
			'index' | 'user' | 'facets'
		> = {
			query: '',
		},
	): Promise<string[]> {
		return this.fetchAlgolia({
			...options,
			query: options.query ?? '',
			size: 0,
			facets: [facet],
			maxValuesPerFacet: options.maxValuesPerFacet ?? 1000,
		}).then((response) =>
			Object.keys(response.facets?.[facet] ?? {}).sort((left, right) =>
				left.localeCompare(right),
			),
		);
	}

	/**
	 * Clears the cache for the Algolia instance. This function is index agnostic, it does
	 * not matter what service is cleared, the entire Algolia instance will be cleared so make
	 * sure that it really needs to be done.
	 *
	 * @note this may be needed after deletion or creation of an entity.
	 */
	readonly clearAlgoliaCache = () => this.algolia.clearCache();

	/**
	 * Resets the cached items collection.
	 */
	protected readonly reset = () => this.items.clear();

	/**
	 * Finds an item located in the cache layer.
	 *
	 * @param id The item id to find in cache
	 */
	protected readonly findInCacheById = (id: string) => this.items.get(id);

	/**
	 * Fetches a collection of items from the cache layer
	 *
	 * @param ids The collection of ids to find
	 */
	protected readonly fetchFromCache = (ids: string[]) =>
		ids.reduce((value, current) => {
			const item = this.findInCacheById(current);
			if (item) value.push(item);
			return value;
		}, [] as ObjectType[]);

	/**
	 * Loads the items that have not already been loaded
	 * into the cache layer of the service.
	 *
	 * @param ids The collection of all item ids that need to be available.
	 */
	differentialFetchByIdentities(ids: string[]) {
		ids = ids.filter((id) => !!id);
		const itemIds = [...Array.from(new Set(ids))];

		const diff = itemIds.filter((id) => this.items.has(id));
		if (!diff.length) {
			return Promise.resolve(Array.from(this.items.values()));
		}

		return this.fetchByIdentities(diff).then((items) => {
			items.forEach((item) => this.items.set(item.id, item));
			return items;
		});
	}

	/**
	 * Returns an observable to listen for doc changes on the provided id.
	 *
	 * @note during implementation, ensure it is being unsubscribed properly
	 * to avoid memory leaks.
	 */
	listenForDocChanges(id: string) {
		const docRef = doc(this.collection, id);
		// skip one for initial invocation when starting the subscription
		return docData<ObjectType>(docRef);
	}

	override logActivity(payload: Omit<LogActivityPayload, 'collectionRef'>) {
		if (!this.collection)
			throw new Error('Collection could not be determined');

		const { entity } = payload;
		if (typeof entity === 'string') {
			return super.logActivity({
				...payload,
				collectionRef: this.collection,
			} as LogActivityPayload);
		}
		return super.logActivity(payload as LogActivityPayload);
	}

	override fetchActivity(payload: Pick<LogActivityPayload, 'entity'>) {
		if (!this.collection)
			throw new Error('Collection could not be determined');

		const { entity } = payload;
		if (typeof entity === 'string') {
			return super.fetchActivity({
				entity,
				collectionRef: this.collection,
			});
		}

		return super.fetchActivity({ entity });
	}
}

type UpdateOptionsBase = {
	addActivityLog: boolean | undefined;
	type?: never | ActivityType;
};

interface UpdateOptionsWithLog extends UpdateOptionsBase {
	addActivityLog: true | undefined;
	type: ActivityType;
}

interface UpdateOptionsWithoutLog extends UpdateOptionsBase {
	addActivityLog: false;
	type?: never;
}

export type UpdateOptions = UpdateOptionsWithLog | UpdateOptionsWithoutLog;
